import { useState, useRef, useEffect } from 'react';
import { Modal, Tab } from 'common/components';
import { GenericTagsTab } from './GenericTagsTab';
import { GenericPromptsTab } from './GenericPromptsTab';
import { GenericPromptTestTab } from './GenericPromptTestTab';
import { useFormContext } from 'react-hook-form';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';
import { toast } from 'react-toastify';

enum GenericTab {
    TAGS = 'tags',
    PROMPTS = 'prompts',
    PROMPT_TEST = 'prompt_test',
}

const genericTabs = [
    { id: GenericTab.TAGS, label: 'Tags' },
    { id: GenericTab.PROMPTS, label: 'Prompts' },
    { id: GenericTab.PROMPT_TEST, label: 'Prompt Test' },
];

export const GenericTagsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const { setValue, trigger, getValues, resetField, formState } = useFormContext<WorkflowConfigFormData>();

    const [activeGenericTab, setActiveGenericTab] = useState<GenericTab>(GenericTab.TAGS);
    const initialSnapshotRef = useRef<{
        genericTags: WorkflowConfigFormData['genericTags'];
        genericPrompts: WorkflowConfigFormData['genericPrompts'];
    } | null>(null);

    useEffect(() => {
        if (isOpen) {
            const genericTags = getValues('genericTags') ?? [];
            const genericPrompts = getValues('genericPrompts') ?? {};
            initialSnapshotRef.current = {
                genericTags: structuredClone(genericTags),
                genericPrompts: structuredClone(genericPrompts),
            };
        }
    }, [isOpen, getValues]);

    const saveResults = () => {
        const errors = formState.errors;

        if (Object.keys(errors).length > 0 || !Object.keys(formState.dirtyFields).length) {
            toast.warning('Please fill all the fields before saving');
            return;
        }
        setValue('isGenericConfigured', true);
        setActiveGenericTab(GenericTab.TAGS);
        onClose();
    };

    const onDone = async () => {
        const genericTagsValues = getValues('genericTags') ?? [];
        const genericTagsPaths: string[] = genericTagsValues.map((_, i) => `genericTags.${i}.value`);
        const genericPromptsPaths: string[] = Object.keys(getValues('genericPrompts')).map(
            (prompt) => `genericPrompts.${prompt}.value`
        );

        await trigger([...genericTagsPaths, ...genericPromptsPaths] as (keyof WorkflowConfigFormData)[]);
        saveResults();
    };

    const handleClose = () => {
        const dirtyFields = formState.dirtyFields;
        const isGenericDirty = !!(dirtyFields.genericTags || dirtyFields.genericPrompts);
        if (isGenericDirty && initialSnapshotRef.current) {
            resetField('genericTags', { defaultValue: initialSnapshotRef.current.genericTags });
            resetField('genericPrompts', { defaultValue: initialSnapshotRef.current.genericPrompts });
            resetField('callTestTranscript');
        }
        setActiveGenericTab(GenericTab.TAGS);
        onClose();
    };

    const onTabChange = (tab: string) => {
        setActiveGenericTab(tab as GenericTab);
    };

    return (
        <Modal
            title='Generic Tags and Prompts'
            isOpen={isOpen}
            onClose={handleClose}
            onDone={onDone}
            closeOnOverlayClick={false}
        >
            <div style={{ minHeight: '440px' }}>
                <Tab variant='buttonStyle' tabs={genericTabs} onTabChange={onTabChange} />
                <div style={{ display: activeGenericTab === GenericTab.TAGS ? 'block' : 'none' }}>
                    <GenericTagsTab />
                </div>
                <div style={{ display: activeGenericTab === GenericTab.PROMPTS ? 'block' : 'none' }}>
                    <GenericPromptsTab isActive={activeGenericTab === GenericTab.PROMPTS} />
                </div>
                {activeGenericTab === GenericTab.PROMPT_TEST && <GenericPromptTestTab />}
            </div>
        </Modal>
    );
};
